import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { toast } from 'sonner';
import { formatDT, formatTimeAgo, formatDuration, formatSpeed, formatDistance, formatWallDateOnly } from '../../utils/timezone';
import { computeDisplayValue, flattenIoElements } from '../../utils/ioMappingValue';
import { getIoValueColorClass } from '../../utils/ioValueColors';
import { ReadOnlyAttachmentsList, VEHICLE_DOC_TYPES } from '../shared/Attachments';
import { useIsMobileOrWebview } from '../../hooks/useIsMobileOrWebview';
import { exportDashboardPdf, exportDashboardHtml, safeName } from '../../utils/tripExport';
import {
  Bluetooth, Satellite, Gauge, Timer, Route, Map as MapIcon, Bell, Wrench, Paperclip, Upload, Link2,
  Clock, Key, Loader2, RefreshCw, Pencil, BookOpen, Truck, BatteryMedium, Navigation,
  SignalHigh, MapPin, ExternalLink, History, Camera, Send, AlertTriangle,
} from 'lucide-react';

// Command button colours — EXACT match to the Live Tracking page (TelemetryPanel CMD_BG).
const CMD_BG = {
  blue: 'bg-blue-600 hover:bg-blue-700', red: 'bg-red-600 hover:bg-red-700',
  green: 'bg-green-600 hover:bg-green-700', yellow: 'bg-yellow-500 hover:bg-yellow-600',
  orange: 'bg-orange-500 hover:bg-orange-600', purple: 'bg-purple-600 hover:bg-purple-700',
  gray: 'bg-gray-500 hover:bg-gray-600', slate: 'bg-gray-500 hover:bg-gray-600',
  teal: 'bg-teal-600 hover:bg-teal-700', pink: 'bg-pink-600 hover:bg-pink-700',
  indigo: 'bg-indigo-600 hover:bg-indigo-700', cyan: 'bg-cyan-600 hover:bg-cyan-700',
  lime: 'bg-lime-600 hover:bg-lime-700', rose: 'bg-rose-600 hover:bg-rose-700',
};
const DONUT_COLORS = { Moving: '#10b981', Idle: '#f59e0b', Stopped: '#3b82f6', 'Parked/Offline': '#94a3b8' };
const TT_PROPS = {
  contentStyle: { backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' },
  labelStyle: { color: 'hsl(var(--foreground))', fontWeight: 600 },
  itemStyle: { color: 'hsl(var(--foreground))' },
  wrapperStyle: { zIndex: 50, outline: 'none' },
};
const BAR_CURSOR = { fill: 'hsl(var(--muted-foreground))', fillOpacity: 0.15 };

const chip = 'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium';
const parseTs = (t) => { try { return new Date(String(t).replace('Z', '+00:00')).getTime(); } catch { return 0; } };
const gmaps = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;
const KPI_GRID = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5' };

// KPI + Section cards — identical styling to VehicleQuickView (Vehicle Dashboard).
const KpiCard = ({ label, value, sub, icon: Icon, color, testId }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-3.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-lg font-bold leading-tight mt-0.5">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground truncate" data-testid={`${testId}-sub`}>{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg shrink-0 ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SectionCard = ({ icon: Icon, title, right, children, testId, className }) => (
  <Card data-testid={testId} className={className}>
    <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-[#2563eb]" /> {title}</CardTitle>
      {right}
    </CardHeader>
    <CardContent className="px-4 pb-4 pt-0">{children}</CardContent>
  </Card>
);

// Trailer & Equipment Dashboard — same shell as the Vehicle Dashboard with two modes:
// GPS-tracked asset → full vehicle-dashboard parity (KPIs, telemetry, charts,
// maintenance, docs, export — no driver/video); BLE beacon asset → observed state,
// attached vehicle, distance travelled while bound and attach/drop history.
export const AssetQuickView = ({ device, open, onClose, onEdit, onOpenHitchLog, onOpenTravelHistory }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { isSubpageEnabled } = useFeatureFlags();
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [hitch, setHitch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [sendingCommand, setSendingCommand] = useState(false);
  const [sendConfirmCmd, setSendConfirmCmd] = useState(null);
  const [tripsLimit, setTripsLimit] = useState('5');
  const [eventsLimit, setEventsLimit] = useState('10');
  const [assetDocs, setAssetDocs] = useState([]);
  const [distanceLoaded, setDistanceLoaded] = useState(false);

  const isBeacon = device?.device_type === 'beacon';
  const isTrailer = (device?.asset_type || '').toLowerCase() === 'trailer';
  const isBuilding = (device?.asset_type || '').toLowerCase() === 'building';
  const kindLabel = isTrailer ? 'Trailer' : isBuilding ? 'Building / Property' : 'Equipment';
  // Portable / battery trackers have no ignition feed → Battery KPI replaces Engine Hours.
  const isPortable = !isBeacon && (device?.device_type === 'asset_tracker' || ['portable', 'magnetic'].includes(device?.installation_type));

  const canTelemetry = isSubpageEnabled('live_tracking_telemetry_data');
  const canEvents = isSubpageEnabled('live_tracking_events');
  const canCommands = isSubpageEnabled('live_tracking_commands');
  const isMobileWebview = useIsMobileOrWebview();
  const actBtn = isMobileWebview ? 'h-11 px-4 text-sm font-semibold' : 'h-7';
  const actIcon = isMobileWebview ? 'w-5 h-5 mr-2' : 'w-3.5 h-3.5 mr-1.5';
  const fleetCommands = useMemo(() => (isBeacon ? [] : (device?.custom_commands || []).filter((c) => c.show_fleet_card !== false)), [device, isBeacon]);

  const executeSendCommand = async (cmd) => {
    setSendingCommand(true);
    try {
      const targetImei = cmd.target_device_imei || device.imei;
      if (cmd.command_mode === 'sequence' && cmd.command_sequence?.length > 0) {
        await api.post('/parsing/commands/send-sequence', {
          device_imei: targetImei,
          steps: cmd.command_sequence.map((s) => ({ command: s.command_text, delay_ms: s.delay_ms || 0 })),
          button_label: cmd.button_label,
        });
        toast.success(`Sequence "${cmd.button_label}" sent (${cmd.command_sequence.length} steps)`);
      } else {
        await api.post('/parsing/commands/send', { device_imei: targetImei, command: cmd.command_text, command_type: 'custom' });
        toast.success(`Command "${cmd.button_label}" sent`);
      }
      setSendConfirmCmd(null);
    } catch (err) {
      toast.error(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSendingCommand(false);
    }
  };

  // Reset stale state the moment the target device changes — the old asset's
  // KPIs/history must never flash while the new one loads.
  useEffect(() => {
    setData(null); setHealth(null); setHitch(null); setAssetDocs([]);
    setLastUpdated(null); setDistanceLoaded(false);
  }, [device?.id, open]);

  useEffect(() => {
    if (!open || !device) return undefined;
    let cancelled = false;
    const load = (freshArg, showSpinner) => {
      if (showSpinner) setLoading(true);
      const done = () => { if (!cancelled && showSpinner) setLoading(false); if (!cancelled) setLastUpdated(new Date().toISOString()); };
      if (isBeacon) {
        // Progressive load: fast health + session list render immediately; the heavier
        // distance-while-bound calculation streams in afterwards without blocking the dialog.
        Promise.all([
          api.get(`/fleet/assets/beacon-health?beacon_id=${device.id}`).then((r) => (r.data?.assets || []).find((a) => a.beacon_device_id === device.id) || null).catch(() => null),
          api.get(`/fleet/assets/${device.id}/hitch-log?limit=25`).then((r) => r.data).catch(() => null),
        ]).then(([h, hl]) => { if (!cancelled) { setHealth(h); setHitch((prev) => (prev?.stats_7d ? prev : hl)); } }).finally(done);
        api.get(`/fleet/assets/${device.id}/hitch-log?limit=25&include_distance=1`)
          .then((r) => { if (!cancelled) setHitch(r.data); })
          .catch(() => {})
          .finally(() => { if (!cancelled) setDistanceLoaded(true); });
      } else {
        api.get(`/fleet/vehicles/${device.id}/dashboard?days=14&fresh=${freshArg ? 1 : 0}`)
          .then((r) => { if (!cancelled) setData(r.data); })
          .catch(() => { if (!cancelled && showSpinner) setData({ trips: [], events: [], stats: null }); })
          .finally(done);
      }
    };
    load(refreshNonce > 0, true);
    const iv = setInterval(() => load(true, false), 60000);
    return () => { cancelled = true; clearInterval(iv); };
    // device?.id (not the object): Fleet's 60s snapshot sync swaps the prop object —
    // that must refresh the fallback merge only, never re-trigger a spinner fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.id, api, isBeacon, refreshNonce]);

  // Documents — same read-only list as the Vehicle Dashboard.
  useEffect(() => {
    if (!open || !device?.id || isBeacon) { setAssetDocs([]); return undefined; }
    let cancelled = false;
    api.get(`/fleet/vehicles/${device.id}/attachments`)
      .then((r) => { if (!cancelled) setAssetDocs(r.data?.attachments || []); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.id, api, isBeacon, refreshNonce]);

  useEffect(() => {
    if (!open || !device?.vehicle_photo?.url) { setPhotoUrl(null); return undefined; }
    let cancelled = false; let objUrl = null;
    api.get(device.vehicle_photo.url.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((r) => { if (!cancelled) { objUrl = URL.createObjectURL(r.data); setPhotoUrl(objUrl); } })
      .catch(() => {});
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, device?.vehicle_photo?.url, api]);

  const dev = useMemo(() => {
    const live = data?.device_live;
    if (!live) return device || {};
    const propTs = parseTs(device?.last_seen || device?.last_gps_time);
    const liveTs = parseTs(live.last_seen || live.last_gps_time);
    return propTs > liveTs ? { ...live, ...device } : { ...device, ...live };
  }, [device, data]);

  const stats = data?.stats;
  const maint = data?.maintenance;
  const trips = useMemo(() => data?.trips || [], [data]);
  const visibleTrips = useMemo(() => trips.slice(0, Number(tripsLimit)), [trips, tripsLimit]);
  const events = useMemo(() => (data?.events || []).slice(0, Number(eventsLimit)), [data, eventsLimit]);
  const currentTrip = useMemo(() => trips.find((t) => t.in_progress) || null, [trips]);
  const lastTrip = useMemo(() => trips.find((t) => t.end_time && !t.in_progress) || null, [trips]);
  const entries = hitch?.entries || [];
  const utilisation = (hitch?.utilisation || []).slice(0, 5);
  const hitchStats = hitch?.stats_7d;
  const binding = health?.binding;
  const lastSeen = isBeacon ? health?.last_seen_at : (dev.last_seen || dev.last_gps_time);

  // Explicit GPS fix status (Digital Matter) — GPS-tracked assets only, same
  // policy as the Vehicle dashboard chip. BLE beacons have no own GPS.
  const gpsFixChip = (() => {
    if (isBeacon) return null;
    const fix = dev.last_gps_fix;
    if (!fix || typeof fix !== 'object') return null;
    const acc = fix.position_accuracy_m != null ? ` · Estimated accuracy: ${fix.position_accuracy_m} m` : '';
    const lastFixT = fix.gps_timestamp ? ` · Last fix: ${formatDT(fix.gps_timestamp)}` : '';
    if (!fix.fix_valid) {
      return { short: 'No valid GPS fix', full: `No valid GPS fix reported${lastFixT ? ` · Last known position retained${lastFixT}` : ''}`, cls: 'text-red-600 border-red-300 bg-red-50 dark:text-red-400 dark:bg-red-950/40' };
    }
    const ageMs = fix.gps_timestamp ? Math.max(0, Date.now() - new Date(fix.gps_timestamp).getTime()) : null;
    const threshold = (dev.offline_threshold_secs || 600) * 1000;
    if (fix.old_fix || ageMs == null || ageMs >= threshold) {
      return { short: `GPS last known · ${fix.gps_timestamp ? formatTimeAgo(fix.gps_timestamp) : 'age unknown'}`, full: `Last known GPS position${acc}${lastFixT}`, cls: 'text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/40' };
    }
    return { short: `GPS fix${fix.fix_3d ? ' · 3D' : ''}${fix.position_accuracy_m != null ? ` · ±${fix.position_accuracy_m}m` : ''}`, full: `GPS fix obtained${fix.fix_3d ? ' · 3D' : ''}${acc}${lastFixT} · Satellites: Not reported`, cls: 'text-blue-600 border-blue-300 bg-blue-50 dark:text-blue-400 dark:bg-blue-950/40' };
  })();
  const ignMapping = (device?.io_mappings || []).find((m) => m.io_type?.toLowerCase() === 'ignition');
  const mappedIgnVal = ignMapping ? (dev.last_io_elements?.[ignMapping.io_id] ?? dev.last_io_elements?.[String(ignMapping.io_id)]) : undefined;
  const ignOn = mappedIgnVal !== undefined
    ? Number(mappedIgnVal) > 0
    : (dev.ignition_on === true || dev.last_io_elements?.['239'] === 1 || dev.last_io_elements?.[239] === 1);
  const lat = isBeacon ? health?.last_location?.latitude : dev.last_latitude;
  const lng = isBeacon ? health?.last_location?.longitude : dev.last_longitude;

  // Telemetry — identical rules to the Vehicle Dashboard (mapped IO with show_fleet_card).
  const telemetryRows = useMemo(() => {
    if (!device || isBeacon) return [];
    const flat = flattenIoElements(dev.last_io_elements);
    return (dev.io_mappings || [])
      .filter((m) => m.show_fleet_card !== false)
      .map((m) => ({ m, val: computeDisplayValue(m, flat) }))
      .filter((r) => r.val !== null && r.val !== undefined && r.val !== '');
  }, [device, dev, isBeacon]);

  const dailySeries = useMemo(() => (stats?.daily || []).map((d) => ({
    ...d,
    label: new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' }),
  })), [stats]);

  const statusDonut = useMemo(() => {
    const s = stats?.status_secs || {};
    return [
      { name: 'Moving', value: Math.round((s.moving || 0) / 3600 * 10) / 10 },
      { name: 'Idle', value: Math.round((s.idle || 0) / 3600 * 10) / 10 },
      { name: 'Stopped', value: Math.round((s.stopped || 0) / 3600 * 10) / 10 },
      { name: 'Parked/Offline', value: Math.round((s.offline || 0) / 3600 * 10) / 10 },
    ].filter((x) => x.value > 0);
  }, [stats]);

  // KPI visibility — Odometer/Engine Hours auto-hide when source is OFF; portable
  // battery trackers (no ignition feed) show a Battery KPI instead of Engine Hours.
  const showOdo = !!(dev.odometer_source && dev.odometer_source !== 'off');
  const showEng = !isPortable && !!(dev.engine_hours_source && dev.engine_hours_source !== 'off');
  const batteryKpi = useMemo(() => {
    if (!isPortable) return null;
    const flat = flattenIoElements(dev.last_io_elements) || {};
    const m = (dev.io_mappings || []).find((x) => /batter/i.test(`${x.io_type || ''} ${x.display_name || ''}`));
    if (m) {
      const v = computeDisplayValue(m, flat);
      if (v !== null && v !== undefined && v !== '') return { value: String(v), sub: m.display_name || 'Battery' };
    }
    const pct = flat['113'] ?? flat[113];
    if (pct != null) return { value: `${pct}%`, sub: 'Battery level' };
    const mv = flat['67'] ?? flat[67];
    if (mv != null) return { value: `${(Number(mv) / 1000).toFixed(2)} V`, sub: 'Battery voltage' };
    const ext = flat['66'] ?? flat[66];
    if (ext != null) return { value: `${(Number(ext) / 1000).toFixed(2)} V`, sub: 'External voltage' };
    return { value: '—', sub: 'Battery' };
  }, [isPortable, dev]);

  // PDF / HTML dashboard export — same branded look as the Vehicle Dashboard export.
  const exportDashboard = (fmt) => {
    const dash = (v) => (v == null || v === '' ? '-' : String(v));
    const two = { columns: [{ label: 'Field', weight: 1 }, { label: 'Value', weight: 2.5 }] };
    if (isBeacon) {
      const sections = [
        {
          heading: `${kindLabel} Details`, ...two,
          rows: [
            ['Name', device.asset_name || device.name],
            ['Identity', dash(device.registration || device.serial_no || device.imei)],
            ['Type', dash(device.asset_type)],
            ['Make / Model', device.asset_make ? `${device.asset_make} ${device.asset_model || ''}${device.asset_year ? ` · ${device.asset_year}` : ''}` : '-'],
            ['Status', device.is_active ? 'Active' : 'Inactive'],
          ],
        },
        {
          heading: 'Beacon Status', ...two,
          rows: [
            ['Battery', health?.battery_percent != null ? `${health.battery_percent}%${health.battery_estimated ? ' (estimated)' : ''}` : '-'],
            ['Signal', health?.last_rssi != null ? `${health.last_rssi} dBm${health?.last_receiver_name ? ` via ${health.last_receiver_name}` : ''}` : '-'],
            ['Last observed', health?.last_seen_at ? formatDT(health.last_seen_at) : '-'],
            ['Attachment', binding?.status === 'attached'
              ? `Attached to ${binding.vehicle_name || 'a vehicle'}${binding.since ? ` since ${formatDT(binding.since)}` : ''}`
              : binding?.status === 'dropped'
                ? `Dropped${binding.vehicle_name ? ` — last carried by ${binding.vehicle_name}` : ''}${binding.ended_at ? ` at ${formatDT(binding.ended_at)}` : ''}`
                : 'Never attached'],
            ['Sessions (7d)', String(hitchStats?.sessions ?? 0)],
            ['Distance (7d)', `${(hitchStats?.distance_km ?? 0).toFixed(1)} km`],
          ],
        },
      ];
      if (utilisation.length) {
        sections.push({
          heading: 'Utilisation by Vehicle',
          columns: [{ label: 'Vehicle', weight: 1.5 }, { label: 'Sessions', weight: 0.6 }, { label: 'Time', weight: 0.8 }, { label: 'Distance', weight: 0.8 }],
          rows: utilisation.map((u) => [u.vehicle_name || '-', String(u.sessions ?? 0), u.total_attached_seconds != null ? formatDuration(u.total_attached_seconds) : '-', u.total_distance_km > 0 ? `${u.total_distance_km.toFixed(1)} km` : '-']),
        });
      }
      if (entries.length) {
        sections.push({
          heading: 'Attach / Drop History',
          columns: [{ label: 'Vehicle', weight: 1.3 }, { label: 'Start', weight: 1 }, { label: 'End', weight: 1 }, { label: 'Duration', weight: 0.7 }, { label: 'Distance', weight: 0.7 }],
          rows: entries.slice(0, 25).map((e) => [
            e.vehicle_name || 'Vehicle',
            e.started_at ? formatDT(e.started_at) : '-',
            e.is_active ? 'ongoing' : (e.ended_at ? formatDT(e.ended_at) : '-'),
            e.duration_seconds != null ? formatDuration(e.duration_seconds) : '-',
            e.distance_km != null ? `${Number(e.distance_km).toFixed(1)} km` : '-',
          ]),
        });
      }
      const name = device.asset_name || device.name;
      const title = `${name} — ${kindLabel} Dashboard`;
      const subtitle = `${device.registration || device.serial_no || device.imei || ''} · exported ${formatDT(Date.now())}`;
      const fname = `${safeName(name)}-dashboard.${fmt}`;
      if (fmt === 'pdf') {
        exportDashboardPdf(title, subtitle, sections, fname).catch(() => toast.error('Export failed'));
        return;
      }
      const opts = {
        kpis: [
          { label: 'Battery', value: health?.battery_percent != null ? `${health.battery_percent}%` : '—', accent: 'green' },
          { label: 'Signal', value: health?.last_rssi != null ? `${health.last_rssi} dBm` : '—', accent: 'blue' },
          { label: 'Sessions (7d)', value: String(hitchStats?.sessions ?? 0), accent: 'purple' },
          { label: 'Distance (7d)', value: `${(hitchStats?.distance_km ?? 0).toFixed(1)} km`, accent: 'amber' },
        ],
      };
      exportDashboardHtml(title, subtitle, sections, fname, opts).catch(() => toast.error('Export failed'));
      return;
    }
    const sections = [{
      heading: `${kindLabel} Details`, ...two,
      rows: [
        ['Name', device.asset_name || device.name], ['Registration / IMEI', dash(device.registration || device.imei)],
        ['Type', dash(device.asset_type)], ['Make / Model', device.asset_make ? `${device.asset_make} ${device.asset_model || ''}${device.asset_year ? ` · ${device.asset_year}` : ''}` : '-'],
        ['Status', device.is_active ? 'Active' : 'Inactive'],
        ...(showOdo ? [['Odometer', dev.computed_odometer != null ? formatDistance(dev.computed_odometer, { decimals: 1 }) : '-']] : []),
        ...(showEng ? [['Engine hours', dev.computed_engine_hours != null ? `${Number(dev.computed_engine_hours).toFixed(1)} h` : '-']] : []),
        ...(batteryKpi ? [['Battery', batteryKpi.value]] : []),
      ],
    }];
    if (stats) {
      sections.push({
        heading: 'Last 7 Days', ...two,
        rows: [['Distance', `${stats.distance_7d ?? 0} km`], ['Trips', String(stats.trips_7d ?? 0)]],
      });
    }
    if (trips.length) {
      sections.push({
        heading: 'Recent Trips',
        columns: [{ label: 'Start', weight: 1 }, { label: 'End', weight: 1 }, { label: 'Distance', weight: 0.6 }, { label: 'Duration', weight: 0.6 }],
        rows: trips.slice(0, 25).map((t) => [
          t.start_time ? formatDT(t.start_time) : '-', t.end_time ? formatDT(t.end_time) : 'ongoing',
          t.distance_km != null ? `${Number(t.distance_km).toFixed(1)} km` : '-',
          t.duration_secs != null ? formatDuration(t.duration_secs) : '-',
        ]),
      });
    }
    if ((data?.events || []).length) {
      sections.push({
        heading: 'Recent Events',
        columns: [{ label: 'Event', weight: 1.6 }, { label: 'Status', weight: 0.6 }, { label: 'Time', weight: 1 }],
        rows: (data.events || []).slice(0, 25).map((evt) => [evt.event_rule_name || evt.event_type || '-', dash(evt.status), evt.triggered_at ? formatDT(evt.triggered_at) : '-']),
      });
    }
    if (assetDocs.length) {
      sections.push({
        heading: 'Documents',
        columns: [{ label: 'Document', weight: 2 }, { label: 'Type', weight: 1 }, { label: 'Expiry', weight: 1 }],
        rows: assetDocs.map((a) => [a.name || 'Attachment', a.doc_type || '-', a.expiry_date ? formatWallDateOnly(a.expiry_date) : '-']),
      });
    }
    const name = device.asset_name || device.name;
    const title = `${name} — ${kindLabel} Dashboard`;
    const subtitle = `${device.registration || device.imei || ''} · exported ${formatDT(Date.now())}`;
    const fname = `${safeName(name)}-dashboard.${fmt}`;
    if (fmt === 'pdf') {
      exportDashboardPdf(title, subtitle, sections, fname).catch(() => toast.error('Export failed'));
      return;
    }
    const opts = {
      kpis: [
        ...(showOdo ? [{ label: 'Odometer', value: dev.computed_odometer != null ? formatDistance(dev.computed_odometer, { decimals: 1 }) : '—', accent: 'blue' }] : []),
        ...(showEng ? [{ label: 'Engine Hours', value: dev.computed_engine_hours != null ? `${Number(dev.computed_engine_hours).toFixed(1)} h` : '—', accent: 'green' }] : []),
        ...(batteryKpi ? [{ label: 'Battery', value: batteryKpi.value, accent: 'green' }] : []),
        { label: 'Trips (7d)', value: String(stats?.trips_7d ?? 0), accent: 'purple' },
        { label: 'Distance (7d)', value: `${stats?.distance_7d ?? 0} km`, accent: 'amber' },
      ],
      charts: (stats?.daily || []).length ? [
        {
          heading: 'Daily Distance (km) — Last 7 Days', color: '#2563eb',
          bars: stats.daily.map((d) => ({ label: new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' }), value: d.km })),
        },
        {
          heading: 'Daily Activity Time (mins) — Last 7 Days', color: '#0d9488',
          bars: stats.daily.map((d) => ({ label: new Date(`${d.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' }), value: d.mins })),
        },
      ] : [],
      donut: statusDonut.length ? {
        heading: 'Status Distribution (hrs) — Last 7 Days',
        unit: 'h',
        slices: statusDonut.map((s) => ({ label: s.name, value: s.value, color: DONUT_COLORS[s.name] })),
      } : null,
    };
    exportDashboardHtml(title, subtitle, sections, fname, opts).catch(() => toast.error('Export failed'));
  };

  if (!device) return null;

  const tripCard = (t, title, testId, live) => (
    <SectionCard icon={Navigation} title={title} testId={testId}
      right={live ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> In progress
        </span>
      ) : (t?.end_time ? <span className="text-[11px] text-muted-foreground">Ended {formatTimeAgo(t.end_time)}</span> : null)}
    >
      <div className="space-y-1">
        <p className="text-sm font-semibold">{formatDT(t.start_time)}</p>
        <p className="text-xs text-muted-foreground">→ {live ? 'ongoing' : formatDT(t.end_time)}</p>
        <div className="flex items-center gap-3 text-xs pt-1">
          {t.distance_km != null && <span className="inline-flex items-center gap-1"><Route className="w-3 h-3" /> {Number(t.distance_km).toFixed(1)} km</span>}
          {t.duration_secs != null && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(t.duration_secs)}</span>}
          {t.max_speed != null && <span>max {formatSpeed(t.max_speed)}</span>}
        </div>
      </div>
    </SectionCard>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="p-0 gap-0 w-screen h-[100dvh] max-w-none rounded-none sm:rounded-lg sm:w-[94vw] sm:max-w-6xl sm:h-[92vh] flex flex-col overflow-hidden" data-testid="asset-quick-view">
        <DialogHeader className="px-4 pt-4 pb-3 border-b shrink-0 text-left">
          <div className="flex items-start gap-3">
            {photoUrl ? (
              <button type="button" onClick={() => setPhotoViewerOpen(true)} title="View photo" className="block shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]" data-testid="asset-quick-view-photo-btn">
                <img src={photoUrl} alt={kindLabel} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border shrink-0 cursor-zoom-in" data-testid="asset-quick-view-photo" />
              </button>
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-[#2563eb]/10 flex items-center justify-center shrink-0" data-testid="asset-quick-view-photo-placeholder">
                <Camera className="w-7 h-7 text-[#2563eb]/50" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="flex flex-wrap items-center gap-2" data-testid="asset-quick-view-title">
                {device.asset_name || device.name}
                {(device.registration || device.serial_no) && (
                  <span className="text-sm font-normal text-muted-foreground font-mono">{device.registration || device.serial_no}</span>
                )}
                <Badge variant="outline" className={`text-xs font-normal ${device.is_active ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400'}`} data-testid="asset-quick-view-active-badge">
                  {device.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-xs" data-testid="asset-quick-view-description">
                {[device.asset_make && `${device.asset_make} ${device.asset_model || ''}`.trim(), device.asset_year, (device.asset_type || kindLabel).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()), `Device: ${device.name}`].filter(Boolean).join(' · ')}
              </DialogDescription>
              <div className="flex flex-wrap items-center gap-1.5 pt-1.5" data-testid="asset-quick-view-chips">
                {isBeacon ? (
                  <span className={`${chip} text-teal-700 border-teal-300 bg-teal-50 dark:text-teal-300 dark:bg-teal-950/40 dark:border-teal-800`} data-testid="asset-quick-view-ble-badge"><Bluetooth className="w-3.5 h-3.5" /> BLE Beacon</span>
                ) : (
                  <span className={`${chip} text-sky-700 border-sky-300 bg-sky-50 dark:text-sky-300 dark:bg-sky-950/40 dark:border-sky-800`} data-testid="asset-quick-view-gps-badge">
                    <Satellite className="w-3.5 h-3.5" /> GPS Tracker{isPortable ? ' · Portable' : ''}
                  </span>
                )}
                {!isBeacon && !isPortable && !isBuilding && !!ignMapping && (
                  <span className={`${chip} ${ignOn ? 'text-emerald-700 border-emerald-300 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/40' : 'text-muted-foreground'}`} data-testid="asset-quick-view-ignition-chip">
                    <Key className="w-3.5 h-3.5" /> Ignition {ignOn ? 'On' : 'Off'}
                  </span>
                )}
                {!isBeacon && dev.last_speed != null && (
                  <span className={chip} data-testid="asset-quick-view-speed-chip"><Gauge className="w-3.5 h-3.5" /> {formatSpeed(dev.last_speed)}</span>
                )}
                {lastSeen && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={`${chip} cursor-default`} data-testid="asset-quick-view-last-seen-chip"><Clock className="w-3.5 h-3.5" /> {formatTimeAgo(lastSeen)}</span>
                      </TooltipTrigger>
                      <TooltipContent className="z-[10001]">Last update received: {formatDT(lastSeen)}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {gpsFixChip && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={`${chip} cursor-default ${gpsFixChip.cls}`} data-testid="asset-quick-view-gps-fix-chip"><Satellite className="w-3.5 h-3.5" /> {gpsFixChip.short}</span>
                      </TooltipTrigger>
                      <TooltipContent className="z-[10001] max-w-[300px]" data-testid="asset-quick-view-gps-fix-tooltip">{gpsFixChip.full}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {lat != null && lng != null && (
                  <a href={gmaps(lat, lng)} target="_blank" rel="noopener noreferrer"
                    className={`${chip} text-[#2563eb] border-[#2563eb]/40 hover:bg-[#2563eb]/10 transition-colors`} data-testid="asset-quick-view-gmaps-link">
                    <MapPin className="w-3.5 h-3.5" /> Open in Google Maps <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
          <div className={`flex flex-wrap ${isMobileWebview ? 'gap-2' : 'gap-1.5'} pt-2`}>
            {onOpenTravelHistory && (
              <Button size="sm" variant="outline" className={actBtn} onClick={onOpenTravelHistory} data-testid="asset-quick-view-travel-history-button">
                <History className={actIcon} /> Travel History
              </Button>
            )}
            {onOpenHitchLog && (
              <Button size="sm" variant="outline" className={actBtn} onClick={onOpenHitchLog} data-testid="asset-quick-view-hitch-log-button">
                {isTrailer ? <Link2 className={actIcon} /> : <Paperclip className={actIcon} />} {isTrailer ? 'Hitch Log' : 'Attachment Log'}
              </Button>
            )}
            {onEdit && (
              <Button size="sm" variant="outline" className={actBtn} onClick={onEdit} data-testid="asset-quick-view-edit-button">
                <Pencil className={actIcon} /> Edit
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className={actBtn} data-testid="asset-quick-view-export-btn">
                  <Upload className={actIcon} /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={() => exportDashboard('pdf')} data-testid="asset-quick-view-export-pdf">PDF</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => exportDashboard('html')} data-testid="asset-quick-view-export-html">HTML</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <span className="ml-auto flex items-center gap-2">
              {lastUpdated && (
                <span className="text-[10px] text-muted-foreground" data-testid="asset-quick-view-updated-ago">Updated {formatTimeAgo(lastUpdated)}</span>
              )}
              <Button size="sm" variant="outline" className={actBtn} onClick={() => setRefreshNonce((n) => n + 1)} disabled={loading} title="Refresh data (also auto-refreshes every 60s)" data-testid="asset-quick-view-refresh-button">
                <RefreshCw className={`${actIcon} ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading && !data && !health && !hitch ? (
            <div className="flex items-center justify-center py-16" data-testid="asset-quick-view-loading"><Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" /></div>
          ) : isBeacon ? (
            /* ════════ BLE BEACON MODE — observed state + distance while bound + history ════════ */
            <div className="space-y-4" data-testid="asset-quick-view-ble-mode">
              {/* BLE KPI row — no odometer / engine hours ever: only what the beacon knows */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="asset-quick-view-ble-kpis">
                <KpiCard label="Battery" value={health?.battery_percent != null ? `${health.battery_percent}%` : '—'} sub={health?.battery_estimated ? 'Estimated' : null} icon={BatteryMedium} color={health?.low_battery ? 'bg-red-500' : 'bg-emerald-500'} testId="asset-quick-view-kpi-battery" />
                <KpiCard label="Signal" value={health?.last_rssi != null ? `${health.last_rssi} dBm` : '—'} sub={health?.last_receiver_name ? `via ${health.last_receiver_name}` : null} icon={SignalHigh} color="bg-sky-500" testId="asset-quick-view-kpi-signal" />
                <KpiCard label="Sessions (7d)" value={hitchStats ? hitchStats.sessions : (distanceLoaded ? '—' : '…')} sub="Vehicle attachments" icon={Truck} color="bg-purple-500" testId="asset-quick-view-kpi-sessions" />
                <KpiCard label="Distance (7d)" value={hitchStats ? `${(hitchStats.distance_km ?? 0).toFixed(1)} km` : (distanceLoaded ? '—' : '…')} sub="While attached to a vehicle" icon={MapIcon} color="bg-teal-500" testId="asset-quick-view-kpi-ble-distance" />
              </div>

              <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                <SectionCard icon={Truck} title="Currently Attached Vehicle" testId="asset-quick-view-attached-section">
                  {binding?.status === 'attached' ? (
                    <div className="flex flex-wrap items-center justify-between gap-2" data-testid="asset-quick-view-attached-vehicle">
                      <div>
                        <p className="text-sm font-semibold text-teal-700 dark:text-teal-400">{binding.vehicle_name || 'Vehicle'}</p>
                        {binding.since && <p className="text-xs text-muted-foreground">Attached since {formatDT(binding.since)} ({formatTimeAgo(binding.since)})</p>}
                      </div>
                      <Badge className="bg-emerald-600 text-white">Attached</Badge>
                    </div>
                  ) : binding?.status === 'dropped' ? (
                    <div className="flex flex-wrap items-center justify-between gap-2" data-testid="asset-quick-view-dropped-state">
                      <div>
                        <p className="text-sm">Last carried by <span className="font-semibold">{binding.vehicle_name || 'a vehicle'}</span></p>
                        {binding.ended_at && <p className="text-xs text-muted-foreground">Dropped {formatDT(binding.ended_at)} ({formatTimeAgo(binding.ended_at)})</p>}
                        {binding.drop_location && (
                          <a href={gmaps(binding.drop_location.latitude, binding.drop_location.longitude)} target="_blank" rel="noopener noreferrer" className="text-xs text-[#2563eb] dark:text-blue-400 hover:underline inline-flex items-center gap-1" data-testid="asset-quick-view-drop-location"><MapPin className="w-3 h-3" /> Drop location <ExternalLink className="w-3 h-3" /></a>
                        )}
                      </div>
                      <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 dark:text-amber-300 dark:border-amber-800 dark:bg-amber-950/40">Dropped</Badge>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground" data-testid="asset-quick-view-never-bound">This {kindLabel.toLowerCase()} has not been attached to any vehicle yet.</p>
                  )}
                  {health?.last_receiver_name && (
                    <p className="text-xs text-muted-foreground pt-2 mt-2 border-t">Last observed by <span className="font-medium text-foreground">{health.last_receiver_name}</span>{health.last_seen_at ? ` · ${formatTimeAgo(health.last_seen_at)}` : ''}</p>
                  )}
                </SectionCard>

                <SectionCard icon={Gauge} title="Utilisation by Vehicle" testId="asset-quick-view-utilisation-section">
                  {utilisation.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="asset-quick-view-utilisation-empty">No utilisation data yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {utilisation.map((u, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 text-sm" data-testid={`asset-quick-view-utilisation-${i}`}>
                          <span className="font-medium truncate flex items-center gap-1.5">{u.vehicle_name}{u.active && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}</span>
                          <span className="text-xs text-muted-foreground shrink-0">
                            {u.sessions} session{u.sessions === 1 ? '' : 's'} · {formatDuration(u.total_attached_seconds)}
                            {u.total_distance_km > 0 ? ` · ${u.total_distance_km.toFixed(1)} km` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>
              </div>

              <SectionCard
                icon={History}
                title="Attach / Drop History"
                right={entries.length > 0 ? <Badge variant="outline" className="text-xs" data-testid="asset-quick-view-history-count">{hitch?.count ?? entries.length}</Badge> : null}
                testId="asset-quick-view-history-section"
              >
                {entries.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-1" data-testid="asset-quick-view-history-empty">No vehicle attachments recorded for this {kindLabel.toLowerCase()}.</p>
                ) : (
                  <div className="divide-y rounded-md border max-h-80 overflow-y-auto">
                    {entries.slice(0, 10).map((e, i) => (
                      <div key={i} className="px-3 py-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-testid={`asset-quick-view-history-${i}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium flex items-center gap-2">
                            <Truck className="w-3.5 h-3.5 text-[#2563eb] shrink-0" />{e.vehicle_name || 'Vehicle'}
                            {e.is_active && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Attached</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">{formatDT(e.started_at)} → {e.is_active ? 'ongoing' : (e.ended_at ? formatDT(e.ended_at) : '—')}</p>
                          {e.ended_reason && <p className="text-[11px] text-muted-foreground">Ended: {e.ended_reason}</p>}
                        </div>
                        <div className="text-xs text-foreground flex items-center gap-3">
                          {e.distance_km != null && <span className="inline-flex items-center gap-1" data-testid={`asset-quick-view-history-km-${i}`}><Route className="w-3 h-3" /> {Number(e.distance_km).toFixed(1)} km</span>}
                          {e.duration_seconds != null && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(e.duration_seconds)}</span>}
                          {e.source_label && <Badge variant="outline" className="text-[10px]">{e.source_label}</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>
          ) : (
            /* ════════ GPS TRACKER MODE — full vehicle-dashboard parity (no driver/video) ════════ */
            <div className="space-y-4" data-testid="asset-quick-view-gps-mode">
              {(() => {
                const n = 2 + (showOdo ? 1 : 0) + (showEng || batteryKpi ? 1 : 0) + (maint ? 1 : 0);
                return (
                  <div className={`grid grid-cols-2 sm:grid-cols-3 ${KPI_GRID[n] || 'lg:grid-cols-4'} gap-2.5`} data-testid="asset-quick-view-kpis">
                    {showOdo && <KpiCard label="Odometer" value={dev.computed_odometer != null ? formatDistance(dev.computed_odometer, { decimals: 1 }) : '—'} sub={dev.odometer_source === 'gps' ? 'GPS calculated' : 'Sensor (IO)'} icon={Gauge} color="bg-blue-500" testId="asset-quick-view-kpi-odometer" />}
                    {showEng && <KpiCard label="Engine Hours" value={dev.computed_engine_hours != null ? `${Number(dev.computed_engine_hours).toFixed(1)} h` : '—'} sub={dev.engine_hours_source === 'ignition' ? 'Ignition time' : 'Sensor (IO)'} icon={Timer} color="bg-teal-500" testId="asset-quick-view-kpi-engine-hours" />}
                    {batteryKpi && <KpiCard label="Battery" value={batteryKpi.value} sub={batteryKpi.sub} icon={BatteryMedium} color="bg-teal-500" testId="asset-quick-view-kpi-battery" />}
                    <KpiCard label="Trips (7d)" value={loading ? '…' : (stats?.trips_7d ?? 0)} icon={Route} color="bg-purple-500" testId="asset-quick-view-kpi-trips" />
                    <KpiCard label="Distance (7d)" value={loading ? '…' : `${(stats?.distance_7d ?? 0).toFixed(1)} km`} icon={MapIcon} color="bg-emerald-500" testId="asset-quick-view-kpi-distance" />
                    {maint && (
                      <KpiCard
                        label="Maintenance Costs"
                        value={`$${(maint.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        sub={`${maint.service_count} service${maint.service_count === 1 ? '' : 's'}`}
                        icon={Wrench} color="bg-orange-500"
                        testId="asset-quick-view-kpi-maintenance"
                      />
                    )}
                  </div>
                );
              })()}

              {/* Current / Last Trip cards — same as the Vehicle Dashboard */}
              {/* A lone trip card spans the full row width — same as the Vehicle Dashboard */}
              {(currentTrip || lastTrip) && (
                <div className={`grid gap-4 grid-cols-1 ${currentTrip && lastTrip ? 'lg:grid-cols-2' : ''}`} data-testid="asset-quick-view-trip-cards">
                  {currentTrip && tripCard(currentTrip, 'Current Trip', 'asset-quick-view-current-trip', true)}
                  {lastTrip && tripCard(lastTrip, 'Last Trip', 'asset-quick-view-last-trip', false)}
                </div>
              )}

              {/* Telemetry — same mapped-IO grid as the Vehicle Dashboard */}
              {canTelemetry && telemetryRows.length > 0 && (
                <SectionCard icon={Gauge} title="Telemetry" testId="asset-quick-view-telemetry-section">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                    {telemetryRows.map(({ m, val }) => (
                      <div key={m.id} className="flex justify-between items-center py-0.5 text-sm border-b border-dashed border-border/50 last:border-0" data-testid={`asset-quick-view-io-${m.id}`}>
                        <span className="text-xs text-muted-foreground truncate max-w-[55%]">{m.display_name}</span>
                        <span className={`text-xs font-semibold ${getIoValueColorClass(val, m.value_colors)}`}>{val}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* Charts + status donut — same as the Vehicle Dashboard */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="asset-quick-view-charts">
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold mb-2">Daily Distance (km) — Last 7 Days</p>
                    <div className="h-36">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dailySeries} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ReTooltip {...TT_PROPS} cursor={BAR_CURSOR} formatter={(v) => [`${v} km`, 'Distance']} />
                          <Bar dataKey="km" fill="#2563eb" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold mb-2">Daily Activity Time (mins) — Last 7 Days</p>
                    <div className="h-36">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dailySeries} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <ReTooltip {...TT_PROPS} cursor={BAR_CURSOR} formatter={(v) => [`${v} mins`, 'Active']} />
                          <Bar dataKey="mins" fill="#0d9488" radius={[3, 3, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                <Card data-testid="asset-quick-view-status-donut">
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold mb-2">Status Distribution (hrs) — Last 7 Days</p>
                    <div className="h-36 flex items-center">
                      <ResponsiveContainer width="55%" height="100%">
                        <PieChart>
                          <Pie data={statusDonut} dataKey="value" nameKey="name" innerRadius={34} outerRadius={55} paddingAngle={2}>
                            {statusDonut.map((s) => <Cell key={s.name} fill={DONUT_COLORS[s.name]} />)}
                          </Pie>
                          <ReTooltip {...TT_PROPS} formatter={(v, n) => [`${v} h`, n]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-1 text-xs flex-1">
                        {statusDonut.map((s) => (
                          <div key={s.name} className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: DONUT_COLORS[s.name] }} />{s.name}</span>
                            <span className="font-medium">{s.value} h</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Commands — identical to the Vehicle Dashboard */}
              {canCommands && fleetCommands.length > 0 && (
                <SectionCard icon={Send} title="Commands" testId="asset-quick-view-commands-section">
                  <div className={`flex flex-wrap justify-center ${isMobileWebview ? 'gap-3' : 'gap-2'}`}>
                    {fleetCommands.map((cmd) => (
                      <Button
                        key={cmd.id}
                        size="sm"
                        className={`${isMobileWebview ? 'h-14 w-full text-base font-semibold rounded-lg' : 'h-9 w-full sm:w-52'} text-white ${CMD_BG[cmd.button_color] || CMD_BG.blue}`}
                        disabled={sendingCommand && sendConfirmCmd?.id === cmd.id}
                        onClick={() => (cmd.requires_confirmation ? setSendConfirmCmd(cmd) : executeSendCommand(cmd))}
                        title={cmd.description || cmd.command_text}
                        data-testid={`asset-quick-view-cmd-${cmd.id}`}
                      >
                        {sendingCommand && sendConfirmCmd?.id === cmd.id
                          ? <Loader2 className={isMobileWebview ? 'w-5 h-5 animate-spin mr-2' : 'w-3.5 h-3.5 animate-spin mr-1.5'} />
                          : <Send className={isMobileWebview ? 'w-5 h-5 mr-2' : 'w-3.5 h-3.5 mr-1.5'} />}
                        <span className="truncate">{cmd.button_label}</span>
                      </Button>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* Maintenance table — same as the Vehicle Dashboard */}
              {maint && (
                <SectionCard
                  icon={Wrench}
                  title="Maintenance History"
                  testId="asset-quick-view-maintenance-section"
                  right={
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-[#2563eb]" onClick={() => { onClose?.(); navigate(`/fleet?tab=maintenance&svc_device=${device.id}`); }} data-testid="asset-quick-view-maintenance-link">
                      View all in Maintenance <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  }
                >
                  {(maint.history || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2" data-testid="asset-quick-view-maintenance-empty">No service history recorded.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Date</TableHead>
                          <TableHead className="text-xs">Task</TableHead>
                          <TableHead className="text-xs text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(maint.history || []).map((h, i) => (
                          <TableRow key={i} data-testid={`asset-quick-view-maintenance-row-${i}`}>
                            <TableCell className="text-xs py-2">{h.completed_date ? String(h.completed_date).slice(0, 10) : '—'}</TableCell>
                            <TableCell className="text-sm py-2 font-medium">{h.task_name || 'Service'}</TableCell>
                            <TableCell className="text-sm py-2 text-right">{h.cost != null ? `$${Number(h.cost).toFixed(2)}` : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </SectionCard>
              )}

              {/* Trips & Events — side by side with Last-N selectors (Vehicle Dashboard parity) */}
              <div className={`grid grid-cols-1 ${canEvents ? 'lg:grid-cols-2' : ''} gap-4`} data-testid="asset-quick-view-bottom-row">
                <SectionCard
                  icon={Route}
                  title={<>Recent Trips {trips.length > 0 && <Badge variant="outline" className="text-xs ml-1" data-testid="asset-quick-view-trips-count">{trips.length} in 14d</Badge>}</>}
                  testId="asset-quick-view-trips-section"
                  right={
                    <Select value={tripsLimit} onValueChange={setTripsLimit}>
                      <SelectTrigger className="h-7 w-[90px] text-xs" data-testid="asset-quick-view-trips-limit"><SelectValue /></SelectTrigger>
                      <SelectContent>{['5', '10', '20', '50'].map((n) => <SelectItem key={n} value={n}>Last {n}</SelectItem>)}</SelectContent>
                    </Select>
                  }
                >
                  {visibleTrips.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-1" data-testid="asset-quick-view-trips-empty">No trips in the last 14 days.</p>
                  ) : (
                    <div className="divide-y rounded-md border max-h-80 overflow-y-auto">
                      {visibleTrips.map((t, i) => (
                        <div key={t.trip_id || i} className="px-3 py-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1" data-testid={`asset-quick-view-trip-${i}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium flex items-center gap-2">
                              {formatDT(t.start_time)}
                              {t.in_progress && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> In progress</span>}
                            </p>
                            <p className="text-xs text-muted-foreground">→ {t.in_progress ? 'ongoing' : formatDT(t.end_time)}</p>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-foreground">
                            {t.distance_km != null && <span className="inline-flex items-center gap-1"><Route className="w-3 h-3" /> {Number(t.distance_km).toFixed(1)} km</span>}
                            {t.duration_secs != null && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDuration(t.duration_secs)}</span>}
                            {t.max_speed != null && <span>max {formatSpeed(t.max_speed)}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </SectionCard>

                {canEvents && (
                  <SectionCard
                    icon={Bell}
                    title="Events"
                    testId="asset-quick-view-events-section"
                    right={
                      <Select value={eventsLimit} onValueChange={setEventsLimit}>
                        <SelectTrigger className="h-7 w-[90px] text-xs" data-testid="asset-quick-view-events-limit"><SelectValue /></SelectTrigger>
                        <SelectContent>{['10', '20', '50', '100'].map((n) => <SelectItem key={n} value={n}>Last {n}</SelectItem>)}</SelectContent>
                      </Select>
                    }
                  >
                    {events.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-1" data-testid="asset-quick-view-events-empty">No events recorded for this {kindLabel.toLowerCase()}.</p>
                    ) : (
                      <div className="divide-y rounded-md border max-h-80 overflow-y-auto">
                        {events.map((evt, i) => {
                          const sev = evt.severity === 'critical' ? 'text-red-600' : evt.severity === 'warning' ? 'text-amber-600' : 'text-slate-500';
                          return (
                            <div key={evt.id || i} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-3 py-2" data-testid={`asset-quick-view-event-${i}`}>
                              <div className="flex items-center gap-2 min-w-0">
                                <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${sev}`} />
                                <span className="text-sm font-medium truncate">{evt.event_rule_name || evt.event_type}</span>
                                {evt.status && <Badge variant="outline" className={`text-[10px] shrink-0 capitalize ${evt.status === 'active' ? 'bg-red-100 text-red-600 border-red-200' : 'bg-green-100 text-green-700 border-green-200'}`}>{evt.status}</Badge>}
                              </div>
                              <span className="text-xs text-muted-foreground">{evt.triggered_at ? formatDT(evt.triggered_at) : '—'}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </SectionCard>
                )}
              </div>

              {/* Documents — read-only list (view/download); uploads live in Edit Asset Details */}
              {assetDocs.length > 0 && (
                <SectionCard icon={Paperclip} title={`Documents (${assetDocs.length})`} testId="asset-quick-view-documents-section">
                  <ReadOnlyAttachmentsList attachments={assetDocs} docTypes={VEHICLE_DOC_TYPES} testPrefix="asset-quick-view-doc" />
                  <p className="text-[11px] text-muted-foreground mt-2">View or download only — add or edit documents in Edit Asset Details.</p>
                </SectionCard>
              )}
            </div>
          )}
        </div>

        {/* Command confirmation dialog */}
        <Dialog open={!!sendConfirmCmd} onOpenChange={(o) => { if (!o) setSendConfirmCmd(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="asset-quick-view-cmd-confirm-dialog">
            {sendConfirmCmd && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" /> Confirm Command</DialogTitle>
                  <DialogDescription>
                    Send "{sendConfirmCmd.button_label}" to {device.asset_name || device.name}?
                    {sendConfirmCmd.description ? ` ${sendConfirmCmd.description}` : ''}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" size="sm" className={isMobileWebview ? 'h-12 text-base' : ''} onClick={() => setSendConfirmCmd(null)} data-testid="asset-quick-view-cmd-cancel">Cancel</Button>
                  <Button size="sm" className={`${isMobileWebview ? 'h-12 text-base' : ''} text-white ${CMD_BG[sendConfirmCmd.button_color] || CMD_BG.blue}`} disabled={sendingCommand} onClick={() => executeSendCommand(sendConfirmCmd)} data-testid="asset-quick-view-cmd-confirm">
                    {sendingCommand ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Send
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Photo viewer */}
        <Dialog open={photoViewerOpen} onOpenChange={setPhotoViewerOpen}>
          <DialogContent className="max-w-2xl" data-testid="asset-quick-view-photo-viewer">
            <DialogHeader><DialogTitle>{device.asset_name || device.name}</DialogTitle></DialogHeader>
            {photoUrl && <img src={photoUrl} alt={kindLabel} className="w-full rounded-lg object-contain max-h-[70vh]" />}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default AssetQuickView;
